import { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const MoodCalendar = ({ darkMode = false }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [moodData, setMoodData] = useState({});
  const [showMoodSelector, setShowMoodSelector] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [currentMonthMoods, setCurrentMonthMoods] = useState([]);

  const moods = [
    { emoji: '😄', label: 'Very Happy', value: 5, color: '#10b981' },
    { emoji: '😊', label: 'Happy', value: 4, color: '#06b6d4' },
    { emoji: '😐', label: 'Neutral', value: 3, color: '#eab308' },
    { emoji: '😔', label: 'Sad', value: 2, color: '#f97316' },
    { emoji: '😢', label: 'Very Sad', value: 1, color: '#ef4444' }
  ];

  useEffect(() => {
    loadMoodData();
  }, []);

  useEffect(() => {
    updateCurrentMonthMoods();
  }, [selectedDate, moodData]);

  const loadMoodData = () => {
    const savedMoodData = localStorage.getItem('moodCalendarData');
    if (savedMoodData) {
      try {
        setMoodData(JSON.parse(savedMoodData));
      } catch (error) {
        console.error('Error parsing mood calendar data:', error);
      }
    }
  };

  const updateCurrentMonthMoods = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const monthMoods = [];
    
    Object.entries(moodData).forEach(([dateKey, mood]) => {
      const date = new Date(dateKey);
      if (date.getFullYear() === year && date.getMonth() === month) {
        monthMoods.push({ date: dateKey, ...mood });
      }
    });
    
    setCurrentMonthMoods(monthMoods);
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dateKey = formatDateKey(date);
    const existingMood = moodData[dateKey];
    
    if (existingMood) {
      setSelectedMood(existingMood);
    } else {
      setSelectedMood(null);
    }
    setShowMoodSelector(true);
  };

  const handleMoodSelect = (mood) => {
    const dateKey = formatDateKey(selectedDate);
    const newMoodData = {
      ...moodData,
      [dateKey]: {
        ...mood,
        timestamp: selectedDate.toISOString(),
        dateKey
      }
    };
    
    setMoodData(newMoodData);
    localStorage.setItem('moodCalendarData', JSON.stringify(newMoodData));
    setSelectedMood(mood);
    
    // Also update the main mood history for consistency
    const moodHistory = JSON.parse(localStorage.getItem('moodHistory') || '[]');
    const existingEntryIndex = moodHistory.findIndex(entry => 
      entry.timestamp.split('T')[0] === dateKey
    );
    
    const newEntry = {
      ...mood,
      timestamp: selectedDate.toISOString()
    };
    
    if (existingEntryIndex >= 0) {
      moodHistory[existingEntryIndex] = newEntry;
    } else {
      moodHistory.unshift(newEntry);
    }
    
    localStorage.setItem('moodHistory', JSON.stringify(moodHistory.slice(0, 50))); // Keep last 50 entries
  };

  const getTileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const dateKey = formatDateKey(date);
    const mood = moodData[dateKey];
    
    if (mood) {
      return (
        <div className="flex items-center justify-center mt-1">
          <div 
            className="w-2 h-2 rounded-full shadow-sm"
            style={{ backgroundColor: mood.color }}
            title={mood.label}
          />
        </div>
      );
    }
    
    return null;
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const dateKey = formatDateKey(date);
    const mood = moodData[dateKey];
    const isSelected = selectedDate && formatDateKey(selectedDate) === dateKey;
    const isToday = formatDateKey(new Date()) === dateKey;
    
    let classes = 'calendar-tile transition-all duration-200 hover:scale-105 ';
    
    if (isSelected) {
      classes += 'ring-2 ring-purple-400 ring-offset-2 ';
    }
    
    if (isToday) {
      classes += 'bg-purple-100 dark:bg-purple-900/30 ';
    }
    
    if (mood) {
      classes += 'has-mood ';
    }
    
    return classes;
  };

  const getMonthStats = () => {
    if (currentMonthMoods.length === 0) return null;
    
    const average = currentMonthMoods.reduce((sum, mood) => sum + mood.value, 0) / currentMonthMoods.length;
    const moodCounts = moods.map(mood => ({
      ...mood,
      count: currentMonthMoods.filter(m => m.value === mood.value).length
    }));
    
    return { average, moodCounts, totalDays: currentMonthMoods.length };
  };

  const stats = getMonthStats();

  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white font-sans">
          📅 Mood Calendar
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Track your daily moods and see patterns over time
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <div className="wellness-card p-6">
            <div className={`mood-calendar ${darkMode ? 'dark-theme' : 'light-theme'}`}>
              <Calendar
                onChange={handleDateClick}
                value={selectedDate}
                tileContent={getTileContent}
                tileClassName={getTileClassName}
                className="w-full"
                showNavigation={true}
                showNeighboringMonth={false}
                locale="en-US"
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Selected Date Info */}
          <div className="wellness-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 font-sans">
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            
            {selectedMood ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl">
                  <span className="text-3xl">{selectedMood.emoji}</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedMood.label}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedMood.value}/5</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMoodSelector(true)}
                  className="w-full wellness-button bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                >
                  Change Mood
                </button>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-gray-500 dark:text-gray-400">No mood logged for this day</p>
                <button
                  onClick={() => setShowMoodSelector(true)}
                  className="w-full wellness-button bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                >
                  Log Mood
                </button>
              </div>
            )}
          </div>

          {/* Month Stats */}
          {stats && (
            <div className="wellness-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 font-sans">
                This Month
              </h3>
              <div className="space-y-4">
                <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {stats.average.toFixed(1)}/5
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Average Mood
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mood Distribution
                  </p>
                  {stats.moodCounts.map(mood => (
                    <div key={mood.value} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{mood.emoji}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">{mood.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-12 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              backgroundColor: mood.color,
                              width: `${(mood.count / stats.totalDays) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {mood.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                  {stats.totalDays} days logged this month
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="wellness-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 font-sans">
              Mood Legend
            </h3>
            <div className="space-y-2">
              {moods.map(mood => (
                <div key={mood.value} className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: mood.color }}
                  />
                  <span className="text-lg">{mood.emoji}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">{mood.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mood Selector Modal */}
      {showMoodSelector && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="wellness-card p-8 max-w-md w-full mx-4 animate-fade-in-scale">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center font-sans">
              How are you feeling on {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}?
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {moods.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => handleMoodSelect(mood)}
                  className={`p-4 rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 border-2 ${
                    selectedMood?.value === mood.value
                      ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                  }`}
                >
                  <div className="text-center space-y-2">
                    <div className="text-3xl">{mood.emoji}</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {mood.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {mood.value}/5
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowMoodSelector(false)}
                className="flex-1 wellness-button bg-gray-500 text-white hover:bg-gray-600"
              >
                Cancel
              </button>
              {selectedMood && (
                <button
                  onClick={() => setShowMoodSelector(false)}
                  className="flex-1 wellness-button bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                >
                  Save Mood
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodCalendar;
